
import React, { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { User } from '@/types/userRole';
import { getManagers, inviteManager, deactivateManager, reactivateManager, getPendingInvites, cancelInvite, convertManagerToUser } from '@/utils/managerService';
import ManagersTable from './ManagersTable';
import InviteManagerForm from './InviteManagerForm';
import PendingInvitesTable from './PendingInvitesTable';
import { Loader2, Users, Mail, History } from 'lucide-react';

const ManagersEditor: React.FC = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('current');
  const [isLoading, setIsLoading] = useState(true);
  const [managers, setManagers] = useState<User[]>([]);
  const [pendingInvites, setPendingInvites] = useState<any[]>([]);
  
  const loadData = async () => {
    setIsLoading(true);
    try {
      const instanceId = 'current-instance'; // In a real app, get this from context or props
      const [managersData, invitesData] = await Promise.all([
        getManagers(instanceId),
        getPendingInvites(instanceId)
      ]);
      
      // Convert Manager[] to User[]
      const userManagers = managersData.map(convertManagerToUser);
      setManagers(userManagers);
      setPendingInvites(invitesData);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load managers data",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    loadData();
  }, []);
  
  const handleInvite = async (name: string, email: string) => {
    try {
      await inviteManager(name, email);
      toast({
        title: "Invitation Sent",
        description: `Invitation email sent to ${email}`,
      });
      await loadData();
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to send invitation",
        variant: "destructive",
      });
    }
  };
  
  const handleCancelInvite = async (inviteId: string) => {
    try {
      await cancelInvite(inviteId);
      toast({
        title: "Invitation Cancelled",
        description: "The invitation has been cancelled",
      });
      await loadData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to cancel invitation",
        variant: "destructive",
      });
    }
  };
  
  const handleDeactivate = async (managerId: string) => {
    try {
      await deactivateManager(managerId);
      toast({
        title: "Manager Deactivated",
        description: "The manager account has been deactivated",
      });
      await loadData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to deactivate manager",
        variant: "destructive",
      });
    }
  };
  
  const handleReactivate = async (managerId: string) => {
    try {
      await reactivateManager(managerId);
      toast({
        title: "Manager Reactivated",
        description: "The manager account has been reactivated",
      });
      await loadData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to reactivate manager",
        variant: "destructive",
      });
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin mr-2 text-primary" />
        <p>Loading managers...</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold">Manager Accounts</h2>
          <p className="text-muted-foreground">Manage login access for your team</p>
        </div>
        <Button 
          onClick={loadData} 
          variant="outline"
          className="flex items-center gap-2"
        >
          <History className="h-4 w-4" /> Refresh
        </Button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2">
          <Tabs defaultValue={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="current" className="flex items-center">
                <Users className="mr-2 h-4 w-4" />
                <span>Current Managers</span>
              </TabsTrigger>
              <TabsTrigger value="pending" className="flex items-center">
                <Mail className="mr-2 h-4 w-4" />
                <span>Pending Invitations</span>
                {pendingInvites.length > 0 && (
                  <span className="ml-2 bg-primary text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                    {pendingInvites.length}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="current" className="space-y-4">
              <ManagersTable 
                managers={managers}
                onDeactivate={handleDeactivate}
                onReactivate={handleReactivate}
              />
            </TabsContent>
            
            <TabsContent value="pending" className="space-y-4">
              <PendingInvitesTable 
                invites={pendingInvites}
                onCancel={handleCancelInvite}
              />
            </TabsContent>
          </Tabs>
        </div>
        
        <div>
          <InviteManagerForm onInvite={handleInvite} />
        </div>
      </div>
    </div>
  );
};

export default ManagersEditor;
